import React, { useEffect, useState } from "react";
import { CalendarPlus, Clock } from "lucide-react";

function calDates(iso, hours = 2) {
  const start = new Date(iso);
  const end = new Date(start.getTime() + hours * 3600 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  return { start: fmt(start), end: fmt(end) };
}

export function googleCalUrl({ title, iso, location, details }) {
  const { start, end } = calDates(iso);
  const p = new URLSearchParams({ action: "TEMPLATE", text: title || "Event", dates: `${start}/${end}`, details: details || "", location: location || "" });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

export function downloadIcs({ title, iso, location, details }) {
  const { start, end } = calDates(iso);
  const esc = (s) => (s || "").replace(/([,;])/g, "\\$1").replace(/\n/g, "\\n");
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//TicketsPro//EN", "BEGIN:VEVENT",
    `UID:${Date.now()}@ticketspro.co.uk`, `DTSTAMP:${start}`, `DTSTART:${start}`, `DTEND:${end}`,
    `SUMMARY:${esc(title || "Event")}`, `LOCATION:${esc(location)}`, `DESCRIPTION:${esc(details)}`,
    "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${(title || "event").replace(/\s+/g, "-").toLowerCase()}.ics`; a.click();
  URL.revokeObjectURL(url);
}

export function AddToCalendar({ event, className = "" }) {
  const [open, setOpen] = useState(false);
  if (!event?.iso) return null;
  return (
    <div className={`relative inline-block ${className}`} data-testid="add-to-calendar">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-full border-2 border-[#16243b] text-[#16243b] px-5 py-2.5 font-medium hover:bg-[#16243b] hover:text-white transition-colors">
        <CalendarPlus size={18} /> Add to calendar
      </button>
      {open && (
        <div className="absolute z-30 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden w-52" onMouseLeave={() => setOpen(false)}>
          <a href={googleCalUrl(event)} target="_blank" rel="noreferrer" data-testid="cal-google"
            className="block px-4 py-2.5 text-sm text-[#16243b] hover:bg-[#f6f8fb]">Google Calendar</a>
          <button onClick={() => { downloadIcs(event); setOpen(false); }} data-testid="cal-ics"
            className="block w-full text-left px-4 py-2.5 text-sm text-[#16243b] hover:bg-[#f6f8fb]">Apple / Outlook (.ics)</button>
        </div>
      )}
    </div>
  );
}

export function Countdown({ iso }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return null;
  const target = new Date(iso).getTime();
  const diff = target - now;
  if (diff <= 0 && diff > -6 * 3600 * 1000) return <Badge label="Happening now" live />;
  if (diff <= 0) return <Badge label="This event has passed" />;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const cell = (v, l) => (
    <div className="text-center">
      <div className="text-2xl font-bold text-[#16243b] tabular-nums">{String(v).padStart(2, "0")}</div>
      <div className="text-[10px] uppercase tracking-wide text-[#8a97a8]">{l}</div>
    </div>
  );
  return (
    <div className="flex items-center gap-4 bg-[#f6f8fb] rounded-xl px-5 py-3" data-testid="ticket-countdown">
      <Clock size={18} className="text-[#2ec9c9]" />
      {cell(d, "days")}{cell(h, "hrs")}{cell(m, "min")}{cell(s, "sec")}
    </div>
  );
}

function Badge({ label, live }) {
  return (
    <div className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium ${live ? "bg-green-100 text-green-700" : "bg-slate-100 text-[#5a6b80]"}`} data-testid="ticket-countdown">
      <Clock size={16} /> {label}
    </div>
  );
}
